import React, { useState } from "react";
import {
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import apiService from "../services/api";
import { AuthScreenShell, AuthCard, AuthCardHeader } from "../components/auth";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("Enlace inválido o incompleto.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      await apiService.resetPassword(token, password);
      setDone(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "No se pudo actualizar la contraseña. Solicita un nuevo enlace.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthScreenShell>
        <AuthCard>
          <AuthCardHeader title="Nueva contraseña" />
          <Alert severity="error">Falta el token en el enlace.</Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <Link component={RouterLink} to="/auth/olvido-contrasena">
              Solicitar nuevo enlace
            </Link>
          </Typography>
        </AuthCard>
      </AuthScreenShell>
    );
  }

  return (
    <AuthScreenShell>
      <AuthCard>
        <AuthCardHeader title="Nueva contraseña" />
        {done ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Contraseña actualizada. Ya puedes iniciar sesión.
            </Alert>
            <Button
              component={RouterLink}
              to="/auth"
              variant="contained"
              fullWidth
              size="large"
            >
              Ir a iniciar sesión
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Nueva contraseña"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label="toggle password"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirmar contraseña"
              type={showPassword ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              margin="normal"
              autoComplete="new-password"
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Guardar"}
            </Button>
          </form>
        )}
      </AuthCard>
    </AuthScreenShell>
  );
};

export default ResetPassword;
